import {
    AppId,
    FCX,
    getRem,
    sanitize,
    usePlatformContext,
    usePlatformFeatureFlag
} from "@datassential/platform-ui-lib";
import { useCategory, useCommonDataContext } from "@core/CommonDataContext";
import Loader from "@components/Loader";
import SelectFiltersProvider, {
    SelectFiltersContextDefaultData,
    useSelectFilters
} from "@vms/SelectFilters/SelectFiltersContext";
import { SelectAmongContextProvider } from "@vms/SelectAmong/SelectAmongContext";
import ContentWrapper from "@components/ContentWrapper/ContentWrapper";
import { FEATURE_AI_SEARCH, HOME_SEARCH_QUERY_PARAM, HomeViewAs } from "@core/constants";
import {
    HomeContextProvider,
    HomeSearchSettingsContextProvider,
    MetricContextProvider,
    useHomeContext
} from "@pages/Home/HomeContext";
import TilesView from "@pages/Home/components/TilesView/TilesView";
import TableView from "@pages/Home/components/TableView/TableView";
import PeopleView from "@pages/Home/components/PeopleView/PeopleView";
import BrandsView from "@pages/Home/components/BrandsView/BrandsView";
import { SelectedKeywordsProvider, } from "@vms/SelectedKeywords/SelectedKeywordsContext";
import { useEffect, useMemo, useState } from "react";
import useQueryState from "@hooks/useQueryState";
import GainView from "@pages/Home/components/GainView/GainView";
import TrendView from "@pages/Home/components/TrendView/TrendView";
import AiResult from "@vms/AiResult";
import { useAiSearchSettingsAndAnswer } from "@api/useAiSearchSettingsAndAnswer";
import { useSettingsSummary } from "@pages/Home/useSettingsSummary";
import Collapsable from "@components/Collapsable";
import { BsChevronUp, BsStars } from "react-icons/bs";
import Divider from "@components/Divider";
import { useSelectedKeywordsSettings } from "@pages/Home/useSelectedKeywordsSettings";
import { useFiltersSettings } from "@pages/Home/useFiltersSettings";
import HomeAmongSettingsApplier from "@pages/Home/HomeAmongSettingsApplier";
import { useGetKeywordBaseData } from "@api/useGetKeywordBaseData";
import { Link } from "react-router-dom";
import { getKeywordProfileLink, Routes } from "@core/routes";

const HomeContent: FCX = () => {
    const { viewAs } = useHomeContext();
    const [categoryId] = useQueryState('categoryId', '');
    const categoryIdNum = Number(categoryId);
    const category = useCategory(categoryIdNum);
    const subCategoriesIds = useMemo(
        () => (category?.subCategories ?? [])
            .map(i => i.id ?? 0),
        [category]
    );

    const {
        setAppliedFilters,
        setSelectedFilters,
    } = useSelectFilters();


    useEffect(() => {
        let newFiltersValue = { ...SelectFiltersContextDefaultData.appliedFilters };

        newFiltersValue.categories = subCategoriesIds;

        setSelectedFilters(newFiltersValue);
        setAppliedFilters(newFiltersValue);
    }, [subCategoriesIds, setAppliedFilters, setSelectedFilters]);

    useSelectedKeywordsSettings();
    useFiltersSettings();

    return (
        <SelectAmongContextProvider>
            <HomeAmongSettingsApplier/>
            {viewAs === HomeViewAs.Tile && <TilesView/>}
            {viewAs === HomeViewAs.Table && <TableView/>}
            {viewAs === HomeViewAs.Trend && <TrendView/>}

            <MetricContextProvider>
                {viewAs === HomeViewAs.People && <PeopleView/>}
                {viewAs === HomeViewAs.Brands && <BrandsView/>}
                {viewAs === HomeViewAs.Gain && <GainView/>}
            </MetricContextProvider>
        </SelectAmongContextProvider>
    );
};


const HomeProviders: FCX = () => {
    const { isLoading } = useCommonDataContext();

    if (isLoading) {
        return <Loader/>;
    }

    return (
        <SelectedKeywordsProvider>
            <SelectFiltersProvider>
                <HomeContextProvider>
                    <HomeContent/>
                </HomeContextProvider>
            </SelectFiltersProvider>
        </SelectedKeywordsProvider>
    );
};

const Home: FCX = () => {
    const getKeywordBaseData = useGetKeywordBaseData();
    const {
        userInfo: {
            products,
            reportProMoreResultsUrl,
        },
    } = usePlatformContext();
    const isFeatureEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH);
    const [rawSearch, setRawSearch] = useQueryState(HOME_SEARCH_QUERY_PARAM, '');
    const [search] = useState(rawSearch);

    const isSearchActive = useMemo(
        () => search.trim().length > 0,
        [search]
    );
    const [isSearchCancelled, setIsSearchCancelled] = useState(!isSearchActive);

    useEffect(() => {
        if (isSearchCancelled) {
            setRawSearch('');
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearchCancelled]);

    useEffect(
        () => {
            document.body.classList.add('is-page-home');
            return () => {
                document.body.classList.remove('is-page-home');
            };
        },
        []
    );

    const {
        data: {
            answer,
            settings,
            isAnswerLoaded,
            isSettingsLoaded,
            isSettingsFinalized,
        },
    } = useAiSearchSettingsAndAnswer(search);

    const isContentReadyForRender = useMemo(
        () => {
            if (!isSearchActive) {
                return true;
            }

            return isSettingsLoaded && isSettingsFinalized;
        },
        [isSearchActive, isSettingsLoaded, isSettingsFinalized]
    );

    const formattedAnswer = useMemo(
        () => {
            const lines = (answer ?? '')
                .split("\n")
                .map(l => l.replace(/^-\s/, ''))
                .filter(line => line.trim().length > 0)
                .filter(line => line.trim().toLowerCase() !== 'redirected')
            ;

            let extraBulletsCount = 0;

            const isSingleSelectedKeyword = settings?.keywordsIds.length === 1;
            const keyword = getKeywordBaseData(settings?.keywordsIds[0] ?? 0);

            const isKeywordBulletVisible = isSingleSelectedKeyword && !!keyword;
            const isMtBulletAccessible = !products[AppId.MenuTrends].isDisabled && settings?.isRequestMtRelated;
            const isRpBulletAccessible = !products[AppId.ReportPro].isDisabled && settings?.isRequestRpRelated;
            const isCorrelationBulletAccessible = settings?.isRequestCorrelationRelated;

            extraBulletsCount += isKeywordBulletVisible ? 1 : 0;
            extraBulletsCount += isMtBulletAccessible ? 1 : 0;
            extraBulletsCount += isRpBulletAccessible ? 1 : 0;
            extraBulletsCount += isCorrelationBulletAccessible ? 1 : 0;


            return (
                <ul className="AiResult__bullets">
                    {lines.map((line, index) => (
                        <li
                            key={line}
                            className={`appears-left del-${index}`}
                        >
                            {line}
                        </li>
                    ))}
                    {isKeywordBulletVisible && (
                        <li className={`appears-left del-${lines.length + extraBulletsCount - 3}`}>
                            Want to know more about <b>{keyword.name}</b>?  Click&nbsp;
                            <Link
                                className="AiResult__link"
                                to={getKeywordProfileLink(keyword.id)}
                            >
                                HERE
                            </Link>
                            &nbsp;to see the full detailed profile
                        </li>
                    )}
                    {isMtBulletAccessible && (
                        <li className={`appears-left del-${lines.length + extraBulletsCount - 2}`}>
                            The chefs at Menu Trends can serve up a better answer for this: click&nbsp;
                            <a
                                className="AiResult__link"
                                href={products[AppId.MenuTrends]?.url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                HERE
                            </a>
                            &nbsp;to go to Menu Trends
                        </li>
                    )}
                    {isRpBulletAccessible && (
                        <li className={`appears-left del-${lines.length + extraBulletsCount - 1}`}>
                            Let's let Report Pro cook up the answer to this. Click&nbsp;
                            <a
                                className="AiResult__link"
                                href={sanitize(`${products[AppId.ReportPro]?.url}/${reportProMoreResultsUrl}${search}`)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                HERE
                            </a>
                            &nbsp;to access our library of reports
                        </li>
                    )}
                    {isCorrelationBulletAccessible && (
                        <li className={`appears-left del-${lines.length + extraBulletsCount}`}>
                            The secret sauce for this request is in another section: click&nbsp;
                            <Link
                                className="AiResult__link"
                                to={Routes.Correlation}
                            >
                                HERE
                            </Link>
                            &nbsp;to run a Correlation analysis
                        </li>
                    )}
                </ul>
            )
        },
        [answer, settings, getKeywordBaseData, products]
    );

    const settingsSummary = useSettingsSummary(settings);

    return (
        <ContentWrapper className="Home">
            <HomeSearchSettingsContextProvider
                data={isSearchCancelled ? null : settings}
                isLoaded={isSettingsLoaded}
                setIsSearchCancelled={setIsSearchCancelled}
            >
                {isSearchActive && isFeatureEnabled && !isSearchCancelled &&
                    <Collapsable defaultShow={true}>
                        <Collapsable.Content>
                            <AiResult
                                className="appears-top"
                                question={search}
                                answer={formattedAnswer}
                                settings={settingsSummary}
                                isAnswerLoading={!isAnswerLoaded}
                                isSettingsLoading={!isSettingsLoaded}
                            />
                        </Collapsable.Content>
                        <Divider
                            className="appears-top del-2"
                            items={[
                                null,
                                <Collapsable.Control
                                    renderLabel={(show) => (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: getRem(8),
                                            fontSize: getRem(12),
                                        }}>
                                            <BsStars style={{
                                                width: getRem(22),
                                                height: getRem(22),
                                            }}/>
                                            {show ? "Hide" : "Show"}&nbsp;AI Summary
                                            <BsChevronUp
                                                style={{
                                                    transform: show ? 'rotate(0deg)' : 'rotate(180deg)',
                                                }}
                                            />
                                        </div>
                                    )}
                                />
                            ]}
                        />
                    </Collapsable>
                }
                {isContentReadyForRender && <HomeProviders/>}
            </HomeSearchSettingsContextProvider>
        </ContentWrapper>
    );
};

export default Home;